type SignUpResponse = {
  user?: {
    identities?: unknown[] | null;
  } | null;
};

/**
 * Supabase intentionally returns a user with no identities when sign-up is
 * attempted for an existing email. Treat that as a verification recovery flow
 * without disclosing account existence to unauthenticated callers.
 */
export function isExistingSignUpResponse(data: SignUpResponse) {
  return Array.isArray(data.user?.identities) && data.user.identities.length === 0;
}

/**
 * Auth returns `user_already_exists` instead of the obfuscated response when a
 * project auto-confirms email or phone sign-ups. Resume setup the same way.
 */
export function isExistingAccountSignUpError(error: unknown) {
  if (!error || typeof error !== 'object') return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return (
    code === 'user_already_exists' ||
    (typeof message === 'string' && /already registered/i.test(message))
  );
}

export function ownerVerificationPath({
  email,
  token,
  expiresAt,
  resumed = false,
}: {
  email: string;
  token: string;
  expiresAt: string;
  resumed?: boolean;
}) {
  const params = new URLSearchParams({ email, owner: token, expires: expiresAt });
  if (resumed) params.set('resumed', 'true');
  return `/verify-email?${params.toString()}`;
}

export function ownerSetupSignInPath({ email, token }: { email: string; token: string }) {
  const redirect = ownerSetupConfirmationPath(token);
  const params = new URLSearchParams({ email, owner: token, redirect });
  return `/login?${params.toString()}`;
}

export function ownerSetupPasswordRecoveryPath({ email, token }: { email: string; token: string }) {
  return `/forgot-password?${new URLSearchParams({ email, owner: token }).toString()}`;
}

export function ownerSetupResetPasswordPath(token: string) {
  return `/reset-password?${new URLSearchParams({ owner: token }).toString()}`;
}

export function ownerSetupConfirmationPath(token: string) {
  return `/confirm-email?${new URLSearchParams({ owner: token }).toString()}`;
}

/**
 * The global organization guard must not sign out an authenticated user while
 * they are completing a server-validated owner onboarding token. The token is
 * still fully validated by the completion RPC before any workspace is created.
 */
export function isOwnerOnboardingContinuationPath(pathname: string, search: string) {
  if (!['/login', '/forgot-password', '/reset-password', '/confirm-email'].includes(pathname)) {
    return false;
  }

  const token = new URLSearchParams(search).get('owner')?.trim() ?? '';
  return /^[A-Za-z0-9_-]{24,512}$/.test(token);
}
